<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("youtubeChannel.videos") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="syncVideos" :loading="syncing">
                    <span class="material-icons" style="font-size: 16px; margin-right: 4px;">sync</span>
                    {{ $t("youtubeChannel.sync") }}
                </a-button>
                <a-button @click="fetchVideos" :loading="loading">
                    <a-icon type="reload" /> {{ $t("youtubeChannel.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="videos-container" v-if="!loading">
            <div class="video-card" v-for="video in videos" :key="video.videoId" @click="openVideoDetail(video)">
                <div class="thumbnail">
                    <img :src="video.thumbnail || video.thumbnailHigh" :alt="video.title" />
                    <span class="comment-count">
                        <a-icon type="message" /> {{ video.commentCount || 0 }}
                    </span>
                    <div class="play-overlay">
                        <span class="material-icons">play_circle</span>
                    </div>
                    <span class="duration" v-if="video.duration">{{ formatDuration(video.duration) }}</span>
                </div>
                <div class="info">
                    <h3 class="video-title">{{ video.title }}</h3>
                    <div class="meta">
                        <span><a-icon type="eye" /> {{ formatNumber(video.viewCount) }}</span>
                        <span><a-icon type="like" /> {{ formatNumber(video.likeCount) }}</span>
                        <span><a-icon type="calendar" /> {{ formatDate(video.publishedAt) }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && videos.length === 0">
            <a-empty :description="$t('youtubeChannel.noVideosFound')" />
        </div>
        <div class="pagination" v-if="totalPages > 1">
            <a-pagination
                :current="page"
                :total="total"
                :page-size="limit"
                @change="onPageChange"
            />
        </div>
    </div>
</template>

<script>
import { YOUTUBE_API } from "../../../../../api/youtube";
import moment from "moment";

export default {
    name: "VideoList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            videos: [],
            loading: false,
            syncing: false,
            page: 1,
            limit: 12,
            total: 0,
            totalPages: 0,
        };
    },
    methods: {
        async fetchVideos() {
            this.loading = true;
            try {
                const response = await YOUTUBE_API.listVideos(this.channel._id, this.page, this.limit);
                this.videos = response.data.videos;
                this.total = response.data.total;
                this.totalPages = response.data.totalPages;
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorFetchingVideos"));
            } finally {
                this.loading = false;
            }
        },
        async syncVideos() {
            this.syncing = true;
            try {
                await YOUTUBE_API.syncVideos(this.channel._id);
                this.$message.success(this.$t("youtubeChannel.videosSynced"));
                await this.fetchVideos();
            } catch (err) {
                this.$message.error(this.$t("youtubeChannel.errorSyncingVideos"));
            } finally {
                this.syncing = false;
            }
        },
        onPageChange(page) {
            this.page = page;
            this.fetchVideos();
        },
        openVideoDetail(video) {
            this.$router.push(`/integrations/youtube-comments/${this.channel._id}/video/${video.videoId}`);
        },
        formatNumber(num) {
            if (!num) return "0";
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1) + "M";
            }
            if (num >= 1000) {
                return (num / 1000).toFixed(1) + "K";
            }
            return num.toString();
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY");
        },
        formatDuration(duration) {
            if (!duration) return "";
            const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
            if (!match) return duration;
            const hours = (match[1] || "").replace("H", "");
            const minutes = (match[2] || "").replace("M", "");
            const seconds = (match[3] || "").replace("S", "");
            let result = "";
            if (hours) result += hours + ":";
            result += (minutes || "0").padStart(2, "0") + ":";
            result += (seconds || "0").padStart(2, "0");
            return result;
        },
    },
    mounted() {
        this.fetchVideos();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .videos-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 24px;
        margin-top: 15px;

        .video-card {
            background: var(--color-bg-subtle);
            border-radius: 12px;
            overflow: hidden;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

                .play-overlay {
                    opacity: 1;
                }
            }

            .thumbnail {
                position: relative;
                height: 180px;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .comment-count {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    background: rgba(0, 0, 0, 0.75);
                    color: #fff;
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .duration {
                    position: absolute;
                    bottom: 10px;
                    right: 10px;
                    background: rgba(0, 0, 0, 0.85);
                    color: #fff;
                    padding: 3px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .play-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(0, 0, 0, 0.4);
                    opacity: 0;
                    transition: opacity 0.2s;

                    .material-icons {
                        font-size: 64px;
                        color: #fff;
                    }
                }
            }

            .info {
                padding: 16px;

                .video-title {
                    margin: 0 0 12px;
                    font-size: 15px;
                    font-weight: 600;
                    line-height: 1.4;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    color: var(--color-text-strong-default);
                }

                .meta {
                    display: flex;
                    gap: 16px;
                    color: var(--color-text-default);
                    font-size: 13px;

                    span {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                    }
                }
            }
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .pagination {
        display: flex;
        justify-content: center;
        margin-top: 24px;
    }
}
</style>
